import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ophelos/ui/components/card';
import { DataTable } from '@ophelos/ui/components/data-table';
import type { ColumnDef } from '@ophelos/ui/lib/table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Sparkles } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { BankStatementLine } from '@/apps/finances/types/bankStatements';
import { formatCurrency } from '@/apps/finances/utils/currency';
import { useCurrency } from '@/hooks/useCurrency';

function SortableHeader({
  label,
  column,
}: {
  label: string;
  column: { getIsSorted: () => false | 'asc' | 'desc'; toggleSorting: (desc: boolean) => void };
}) {
  const sorted = column.getIsSorted();
  return (
    <Button variant="ghost" size="sm" className="-ml-2 h-7 px-2 text-xs" onClick={() => column.toggleSorting(sorted === 'asc')}>
      {label}
      {sorted === 'asc' ? (
        <ArrowUp className="ml-1 size-3" />
      ) : sorted === 'desc' ? (
        <ArrowDown className="ml-1 size-3" />
      ) : (
        <ChevronsUpDown className="ml-1 size-3 opacity-50" />
      )}
    </Button>
  );
}

const buildColumns = (currency: string): ColumnDef<BankStatementLine>[] => [
  {
    accessorKey: 'id',
    header: 'ID',
    cell: ({ row }) => <span className="truncate max-w-[100px] block">{row.original.id}</span>,
    enableSorting: false,
  },
  {
    accessorKey: 'transaction_date',
    header: ({ column }) => <SortableHeader label="Date" column={column} />,
    cell: ({ row }) => {
      const d = row.original.transaction_date;
      return d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    },
  },
  {
    accessorKey: 'amount_millicents',
    header: ({ column }) => <SortableHeader label="Amount" column={column} />,
    cell: ({ row }) => {
      const mc = row.original.amount_millicents;
      return mc != null ? formatCurrency(mc, currency) : '—';
    },
    meta: { align: 'right' as const },
  },
];

interface BankLineMatchPickerCardProps {
  lines: BankStatementLine[];
  onFindMatches: (lineId: string) => void;
  onDismiss?: () => void;
  isLoading?: boolean;
}

export function BankLineMatchPickerCard({ lines, onFindMatches, onDismiss, isLoading = false }: BankLineMatchPickerCardProps) {
  const { currency } = useCurrency();
  const columns = useMemo(() => buildColumns(currency), [currency]);
  const [selectedLineId, setSelectedLineId] = useState<string>('');
  const hasLines = lines.length > 0;
  const canSubmit = hasLines && selectedLineId !== '' && !isLoading;

  return (
    <Card aria-label="Find matches for a bank line" className="py-3 bg-soft">
      <CardHeader className="flex items-center gap-2 px-3">
        <Sparkles className="size-3.5 text-brand-600 dark:text-brand-400 shrink-0" aria-hidden />
        <CardTitle>Find matches for a bank line</CardTitle>
      </CardHeader>
      <CardContent className="px-2">
        {!hasLines ? (
          <p className="text-muted-foreground text-xs mb-2">No unmatched lines.</p>
        ) : (
          <DataTable
            columns={columns}
            data={lines}
            onRowClick={(row) => setSelectedLineId(selectedLineId === row.id ? '' : row.id)}
            getRowId={(row) => row.id}
            selectedRowId={selectedLineId}
            rowSize="xs"
            hidePagination
            enableColumnVisibility={false}
          />
        )}

        <div className="flex gap-2 mt-2">
          <Button
            size="sm"
            onClick={() => selectedLineId && onFindMatches(selectedLineId)}
            disabled={!canSubmit}
            className="shrink-0"
            aria-label={selectedLineId ? `Find matches for ${selectedLineId}` : 'Select a line to find matches'}
          >
            Find matches
          </Button>
          {onDismiss && (
            <Button variant="ghost" size="sm" onClick={onDismiss} disabled={isLoading} aria-label="Dismiss">
              Dismiss
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
